import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, Star, Sparkle, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function Subscription() {
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [pendingPlan, setPendingPlan] = useState(null); // {id, name, isDowngrade}

  const load = () => {
    Promise.all([api.get("/subscription/plans"), api.get("/subscription")]).then(([p, s]) => {
      setPlans(p.data.plans); setCurrent(s.data);
    });
  };
  useEffect(() => { load(); }, []);

  const change = async (plan_id) => {
    const target = plans.find(p => p.id === plan_id);
    const currentIdx = plans.findIndex(p => p.id === current?.plan?.id);
    const targetIdx = plans.findIndex(p => p.id === plan_id);
    const isDowngrade = targetIdx >= 0 && currentIdx >= 0 && targetIdx < currentIdx;
    const isCancellation = plan_id === "free";
    setPendingPlan({ id: plan_id, name: target?.name || plan_id, isDowngrade, isCancellation });
  };

  const confirmChange = async () => {
    if (!pendingPlan) return;
    const plan_id = pendingPlan.id;
    setPendingPlan(null);
    try {
      await api.post("/subscription/change", { plan_id });
      toast.success("Plan updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Plan change failed"); }
  };

  if (!current) return <PageShell title="Subscription"><div className="h-64 glass animate-pulse" /></PageShell>;

  const { plan, usage } = current;
  const limitBar = (used, limit) => {
    if (limit < 0) return { pct: 0, label: `${used.toLocaleString()} / ∞` };
    const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
    return { pct, label: `${used.toLocaleString()} / ${limit.toLocaleString()}` };
  };

  return (
    <PageShell title="Subscription" subtitle={`Current plan: ${plan.name}`}>
      <GlassCard strong className="p-6 md:p-8 mb-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline">Current plan</div>
            <h2 className="text-4xl font-semibold tracking-tighter mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{plan.name}</h2>
            <div className="text-sm text-[var(--text-2)] mt-1">{plan.billing}</div>
          </div>
          <div className="text-end">
            <div className="overline">Price</div>
            <div className="text-3xl font-mono font-semibold mt-1">{plan.price_usd === null ? "Custom" : `$${plan.price_usd}`}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
          {[
            { key: "contracts", label: "Contracts", limit: plan.limits.contracts },
            { key: "analyses", label: "AI analyses / mo", limit: plan.limits.analyses_per_month },
            { key: "bir", label: "Impact reports / mo", limit: plan.limits.bir_per_month },
            { key: "seats", label: "Team seats", limit: plan.limits.team_seats },
          ].map((x) => {
            const { pct, label } = limitBar(usage[x.key] ?? 0, x.limit);
            return (
              <div key={x.key}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[var(--text-2)]">{x.label}</span>
                  <span className="font-mono text-[var(--text-3)]">{label}</span>
                </div>
                <Progress value={pct} className="h-1.5" />
              </div>
            );
          })}
        </div>
      </GlassCard>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((p, i) => {
          const isCurrent = p.id === plan.id;
          return (
            <motion.div key={p.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
              <GlassCard hover={!isCurrent} className={`p-6 h-full flex flex-col relative ${p.popular ? "border-[var(--accent)]" : ""}`} strong={p.popular}>
                {p.popular && (
                  <span className="absolute -top-3 start-5 chip chip-low inline-flex items-center gap-1"><Star size={10} weight="fill" /> Most popular</span>
                )}
                <div className="overline">{p.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-4xl font-semibold tracking-tighter" style={{ fontFamily: 'var(--font-heading)' }}>{p.price_usd === null ? "Custom" : `$${p.price_usd}`}</span>
                  {p.price_usd !== null && <span className="text-xs text-[var(--text-3)]">/ mo</span>}
                </div>
                <div className="text-xs text-[var(--text-3)] mb-5">{p.billing}</div>

                <ul className="space-y-1.5 flex-1 mb-5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-[var(--text-2)]">
                      <CheckCircle size={12} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => change(p.id)}
                  disabled={isCurrent}
                  data-testid={`plan-select-${p.id}`}
                  variant={isCurrent ? "outline" : "default"}
                  className={`w-full gap-2 ${isCurrent ? "glass-subtle border-[var(--border-strong)]" : ""}`}
                >
                  {isCurrent ? "Current plan" : p.price_usd === null ? "Contact sales" : <>Switch to {p.name} <ArrowRight size={12} /></>}
                </Button>
              </GlassCard>
            </motion.div>
          );
        })}
      </div>

      <ConfirmDialog
        open={!!pendingPlan}
        onOpenChange={(v) => { if (!v) setPendingPlan(null); }}
        title={pendingPlan?.isCancellation
          ? "Cancel paid subscription?"
          : pendingPlan?.isDowngrade
            ? `Downgrade to ${pendingPlan?.name}?`
            : `Switch to ${pendingPlan?.name}?`}
        description={pendingPlan?.isCancellation
          ? "You'll drop to the Free tier at the end of your current billing cycle. Contracts above the Free limit will become read-only — they won't be deleted. You can re-upgrade anytime."
          : pendingPlan?.isDowngrade
            ? `You'll move to ${pendingPlan?.name} at the end of your current billing cycle. Any usage currently above the lower limits will become read-only until usage drops below the cap.`
            : `Your card will be charged the prorated amount immediately and the upgraded limits take effect right away.`}
        actionLabel={pendingPlan?.isCancellation ? "Cancel subscription" : pendingPlan?.isDowngrade ? "Downgrade plan" : "Confirm upgrade"}
        destructive={!!(pendingPlan?.isCancellation || pendingPlan?.isDowngrade)}
        onConfirm={confirmChange}
        testId="plan-change-confirm-dialog"
      />
    </PageShell>
  );
}
